import { CONVERSATION_CLOSED_CODE } from "@nexus/types/domain"
import { createAsyncThunk } from "@reduxjs/toolkit"

import type { EmbedRequestFailure } from "@/common/services/chat"

import type { EmbedRootState } from "../embedSelectors"
import { reopenClosedEmbedChat } from "./reopenClosedEmbedChat"
import {
  sendEmbedMessage,
  type SendEmbedMessageParams,
  type SendEmbedMessageResult
} from "./sendMessage"

type SendEmbedMessageRejected = ReturnType<typeof sendEmbedMessage.rejected>

/**
 * Send a visitor's message, recovering once when its conversation was deleted
 * while the tab was open.
 *
 * On `CONVERSATION_CLOSED` the tab re-initialises into a fresh conversation and
 * the SAME message — same content, same id — is sent there. Any other refusal
 * (a 500, a network failure, an authentication 403) keeps its existing handling:
 * it rejects exactly as a plain send did, with no re-initialisation.
 *
 * 🔴 The resend is bounded at ONE by construction, not by a counter. It goes
 * through `sendEmbedMessage`, never back through this thunk, so a second refusal
 * of any kind — `CONVERSATION_CLOSED` included — is terminal and rejects.
 *
 * The message id is reused on purpose: the refused send wrote nothing (the backend
 * refuses before it resolves the chat), so the resend is the message's only write,
 * and a resend that somehow collided with a written row would fail loudly on the
 * id rather than land a duplicate.
 *
 * The fresh chat id is read off `activeChatId` after the reopen rather than off the
 * reopen's own payload, so a reopen that a concurrent refusal already completed is
 * adopted the same way as this one.
 */
export const deliverEmbedMessage = createAsyncThunk<
  SendEmbedMessageResult,
  SendEmbedMessageParams,
  { state: EmbedRootState; rejectValue: EmbedRequestFailure }
>("embedChat/deliverMessage", async (params, { dispatch, getState, rejectWithValue }) => {
  const sent = await dispatch(sendEmbedMessage(params))
  if (sendEmbedMessage.fulfilled.match(sent)) return sent.payload

  const refusal = failureOf(sent)
  if (refusal.code !== CONVERSATION_CLOSED_CODE) return rejectWithValue(refusal)

  await dispatch(reopenClosedEmbedChat({ closedChatId: params.chatId }))

  const freshChatId = getState().embedChat.activeChatId
  if (freshChatId === null || freshChatId === params.chatId) return rejectWithValue(refusal)

  const resent = await dispatch(sendEmbedMessage({ ...params, chatId: freshChatId }))
  if (sendEmbedMessage.fulfilled.match(resent)) return resent.payload

  return rejectWithValue(failureOf(resent))
})

function failureOf(action: SendEmbedMessageRejected): EmbedRequestFailure {
  return action.payload ?? { message: action.error.message ?? "Failed to send message", code: null }
}
